/**
 * Mark the active branch of a Storyblok-driven navigation tree.
 *
 * Matches a nav item as `currentPage` when:
 *   - its Storyblok UUID equals the current story UUID, OR
 *   - its resolved slug equals the current page slug.
 *
 * Bubbles `open: true` up to every ancestor of a current page or an
 * ancestor whose slug is a path-prefix of the current pathname (handles
 * deep leaf pages that are not themselves nav entries).
 *
 * Pure — no React hooks, safe to call from `useMemo` or anywhere else.
 */

import { stripSlashes, stripSiteRoot, stripLangPrefix } from "./site-root"

const slugify = (path, { siteRoot = "", language = "" } = {}) => {
  if (!path) return ""
  let s = stripSlashes(path)
  s = stripSiteRoot(s, siteRoot)
  s = stripLangPrefix(s, language)
  // "home" is Storyblok's root slug
  if (s === "home") return ""
  return s
}

const isAncestorSlug = (parentSlug, childSlug) => {
  // The root ("") is the ancestor only of itself — never auto-mark every
  // top-level item active.
  if (parentSlug === "") return childSlug === ""
  if (parentSlug === childSlug) return true
  return childSlug.startsWith(`${parentSlug}/`)
}

/**
 * Homepage paths ("/", "/<lang>/") shouldn't highlight anything in the nav.
 */
const isHomepagePath = (pathname, language) => {
  if (!pathname) return true
  if (pathname === "/" || pathname === "") return true
  if (language && (pathname === `/${language}` || pathname === `/${language}/`))
    return true
  return false
}

const markCurrentNav = ({
  items,
  currentPath,
  currentUuid,
  language = "",
  siteRoot = process.env.GATSBY_SITE_ROOT || "",
} = {}) => {
  if (!Array.isArray(items)) return items

  // The helper still marks items by UUID on homepage so the top-bar
  // main-item (whose UUID matches the home story) keeps its highlight.
  // The drawer-level suppression happens in item.js and layer.js where
  // isHomepagePath gates the visual states.

  const currentSlug = slugify(currentPath, { siteRoot, language })

  const walk = (nodes) =>
    nodes.map((item) => {
      const itemUuid = item && item.url ? item.url.id : null
      const itemSlug = slugify(item && item.url ? item.url.cached_url : "", {
        siteRoot,
        language,
      })

      const isCurrentPage =
        (!!itemUuid && itemUuid === currentUuid) ||
        (!!itemSlug && itemSlug === currentSlug)
      const isAncestor =
        !isCurrentPage && !!itemSlug && isAncestorSlug(itemSlug, currentSlug)

      const newItem = { ...item, currentPage: !!isCurrentPage }
      let openFromDescendants = false

      if (Array.isArray(item.children) && item.children.length > 0) {
        newItem.children = walk(item.children)
        openFromDescendants = newItem.children.some(
          (c) => c.currentPage || c.open
        )
      }

      if (Array.isArray(item.extras) && item.extras.length > 0) {
        newItem.extras = walk(item.extras)
        openFromDescendants =
          openFromDescendants ||
          newItem.extras.some((e) => e.currentPage || e.open)
      }

      if (isCurrentPage || isAncestor || openFromDescendants) {
        newItem.open = true
      }

      return newItem
    })

  return walk(items)
}

export default markCurrentNav
export { slugify, isAncestorSlug, isHomepagePath, stripSlashes }
